import { Text, Box, Flex, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState, useCallback } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { PortfolioCovenantTable } from '../entity/portfolioCovenant/PortfolioCovenantTable';
import {
  AddIconButton,
  ResetIconButton,
  SaveIconButton,
} from 'packages/common-frontend/components/IconButton';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { useEvaluatePortfolioCovenantsMutation } from 'packages/creditAgreements/requests/requests.gql.generated';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const createPortfolioCovenantPropertyShell = (): CreditAgreement.DomainEntity.PortfolioCovenantProperty => ({
  name: 'Portfolio Covenant',
  covenantKey: 'covenantKey',
  type: CreditAgreement.DomainEntity.PropertyType.PortfolioCovenantProperty,
  value: 'description of covenant',
  _isWrittenIn_: true,
});

const addPortfolioCovenant = ({
  wipConfig,
  basePropertyKey,
}: {
  wipConfig: WipConfig;
  basePropertyKey: string;
}) => {
  const cov = createPortfolioCovenantPropertyShell();

  const portfolioCovenants = getIn(wipConfig.formProps.values, basePropertyKey);

  wipConfig.formProps.setFieldValue(basePropertyKey, [
    ...portfolioCovenants,
    cov,
  ]);
};

export const PortfolioCovenants: React.FC<{
  portfolioCovenants: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
  wipConfig?: WipConfig;
}> = ({ portfolioCovenants, wipConfig }) => {
  const toast = useToast();
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const basePropertyKey = 'portfolioCovenants';

  const [triggerDate, setTriggerDate] = useState<Util.FullDateStr>(
    commonDate.dateToFullDateString(new Date(), clientTimeZone),
  );

  const [
    evaluatePortfolioCovenants,
    { loading: isEvaluatePortfolioCovenantsQALoading },
  ] = useEvaluatePortfolioCovenantsMutation({
    onCompleted: () => {
      toast({
        title:
          'Initiated portfolio covenants evaluation based on latest SAVED WIP.  Please check your email in a few minutes.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title:
          'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        status: 'error',
        position: 'top',
      });
    },
  });

  const handleEvaluatePortfolioCovenantsQA = useCallback(
    async (wc: WipConfig) => {
      const creditAgreementId = getIn(wc.formProps.values, 'id');
      const creditFacilityId = getIn(wc.formProps.values, 'creditFacilityId');
      const portCovs = getIn(
        wc.formProps.values,
        basePropertyKey,
      ) as CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
      const covenantKeys = portCovs
        .filter((portCov) => !!portCov._config_)
        .map((portCov) => portCov.covenantKey);

      if (covenantKeys.length) {
        await evaluatePortfolioCovenants({
          variables: {
            evaluatePortfolioCovenantsRequest: {
              covenantKeys,
              creditAgreementId,
              creditFacilityId,
              triggerDate,
              mode: 'QA',
            },
          },
        });
      } else {
        toast({
          title: 'No portfolioCovenant configs exist to QA.',
          status: 'warning',
          isClosable: true,
          duration: 5000,
          position: 'top',
        });
      }
    },
    [evaluatePortfolioCovenants, triggerDate, toast],
  );

  if (
    !wipConfig?.isMutateAllowed(basePropertyKey) &&
    !_.compact(Object.values(portfolioCovenants)).length
  ) {
    return null;
  }

  return (
    <Box mt="40px">
      <StickyHeaderSection labelText="Portfolio Covenants">
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="3"
          >
            <Text fontStyle="italic" mr="3">
              &quot;triggerDate&quot; mock:
            </Text>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(triggerDate)}
              setSelectedDate={(date: Date) => {
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                setTriggerDate(fullDateStr);
              }}
            />
            <SaveIconButton
              ariaLabel="save config"
              label="Save Configuration"
              onClick={() => {
                wipConfig.saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: wipConfig.formProps.values.id,
                      creditFacilityId:
                        wipConfig.formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(
                        wipConfig.formProps.values,
                      ),
                    },
                  },
                });
              }}
              ml="2"
            />
            <ResetIconButton
              ariaLabel="qa portfolio covenants"
              label="QA Portfolio Covenants"
              onClick={() => handleEvaluatePortfolioCovenantsQA(wipConfig)}
              isDisabled={isEvaluatePortfolioCovenantsQALoading}
            />
          </Flex>
        )}

        <Flex alignItems="center" mb="2">
          <Text fontWeight="bold" fontSize="20px">
            Properties
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              <AddIconButton
                ariaLabel="add portfolio covenant"
                label="Add Portfolio Covenant"
                onClick={() => {
                  addPortfolioCovenant({
                    wipConfig,
                    basePropertyKey,
                  });
                }}
              />
            </>
          )}
        </Flex>

        <PortfolioCovenantTable
          portfolioCovenants={portfolioCovenants}
          basePropertyKey={basePropertyKey}
          wipConfig={wipConfig}
          triggerDate={triggerDate}
        />
      </StickyHeaderSection>
    </Box>
  );
};
